import { useEffect, useState } from "react";
import AudioUploaderV2 from "../../../../components/forms/AudioUploader/AudioUploaderV2";
import TabbedPanel from "../../../../components/TabbedPanel/TabbedPanel";
import DEBUG_LOGGER from "../../../../helpers/DebugLogger";
import { convertUniqueFilenameToOriginal, getCampaignAudio } from "../../../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import { IconJsxer } from "../../../../helpers/IconHelper";
import WaveSurfer from "wavesurfer.js";

const AudioUploaderContent = (props) => {
    const varVal = props.audioFileData;
    const [audioSrc, setAudioSrc] = useState(varVal?.srcUrl || "");

    const [busy, setBusy] = useState(false);
    const [campaignAudio, setCampaignAudio] = useState([]);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const handleLibraryAudioSelect = (audioFileData) => {
        if (!busy && props.isOpenCanMakeChanges) {
            varVal.srcUrl = audioFileData.url;
            setAudioSrc(audioFileData.url);
            varVal.originalFile = audioFileData.filename;
            varVal.filename = audioFileData.filename;
            varVal.newFile = null;
            varVal.isDefault = false;

            // Trigger both callbacks to update UI and right sidebar preview
            if (props.onAudioSelected) {
                props.onAudioSelected(varVal);
            }
            if (props.onAudioChanged) {
                props.onAudioChanged(varVal);
            }
        }
    };

    const [currentTab, setCurrentTab] = useState('upload');

    // Load campaign audio when switching to library tab
    useEffect(() => {
        const loadCampaignAudio = async () => {
            if (currentTab === 'library' && props.campaignData?._id && campaignAudio.length === 0 && !loadingAudio) {
                setLoadingAudio(true);
                try {
                    const audio = await getCampaignAudio(props.campaignData._id);
                    setCampaignAudio(audio);
                } catch (error) {
                    DEBUG_LOGGER.error('Failed to load campaign audio:', error);
                } finally {
                    setLoadingAudio(false);
                }
            }
        };
        loadCampaignAudio();
    }, [currentTab, props.campaignData?._id, campaignAudio.length, loadingAudio]);

    const handleTabSwitch = (tabId, tabData) => {
        setCurrentTab(tabData.id);
    };

    const tabData = [
        { label: 'Upload New', id: 'upload' },
        { label: 'Audio Library', id: 'library' }
    ];

    const AudioPreview = (props) => {
        const [isPlaying, setIsPlaying] = useState(false);
        const [waveformId, setWaveformId] = useState(`audio-lib-waveform-preview-${props.index ?? ''}`);

        const [wavesurferInstance, setWavesurferInstance] = useState(null);
        useEffect(() => {
            if (props.src) {
                    // console.log('[timeline, useEffect] Creating waveform for', waveformId, 'with URL', props.src);
                    const waveSurfer = WaveSurfer.create({
                        container: `#${waveformId}`,
                        waveColor: '#BCA4FF',
                        progressColor: '#9146FF',
                        url: props.src,
                        height: 20,
                        normalize: true,
                        hideScrollbar: true,
                    });
                    setWavesurferInstance(waveSurfer);
                    return () => {
                        // console.log('[timeline, useEffect] Destroying waveform for', generatedWaveformContainerId);
                        waveSurfer.destroy();
                        setWavesurferInstance(null);
                    }
            }
        }, [props.src]);

        useEffect(() => {
            if (wavesurferInstance) {
                const playHandler = () => { setIsPlaying(true); };
                const stopHandler = () => { setIsPlaying(false); };
                wavesurferInstance.on('play', playHandler);
                wavesurferInstance.on('finish', stopHandler);
                wavesurferInstance.on('pause', stopHandler);
            }
            return () => {
                if (wavesurferInstance) {
                    wavesurferInstance.un('play');
                    wavesurferInstance.un('finish');
                    wavesurferInstance.un('pause');
                    wavesurferInstance.stop(); // Ensure audio is stopped when component unmounts or wavesurfer instance changes
                    wavesurferInstance.destroy(); // Clean up wavesurfer instance
                }
                setIsPlaying(false);
            };
        }, [wavesurferInstance]);

        if (!props.src) return null;

        return (
            <div className='fl-row compact' style={{cursor: 'default'}} onClick={(e) => e.stopPropagation()}>
                <div className={`play-button-square${wavesurferInstance ? '' : ' btn-off'}`} onClick={wavesurferInstance ? () => {wavesurferInstance.isPlaying() ? wavesurferInstance.stop() : wavesurferInstance.play()} : null}>
                    {IconJsxer.GetIcon(isPlaying ? IconJsxer.ICONS.stop : IconJsxer.ICONS.play, IconJsxer.ICON_STYLES.campaignPanelInfoPill)}
                </div>
                <div id={`${waveformId}`} className={`audio-waveform-preview-holder`}>
                    {/* Waveform preview will be rendered here */}
                </div>
            </div>
        );
    }

    const renderLibraryTab = () => (
        <div className="library-tab-content" onWheel={(e) => e.stopPropagation()}>
            {loadingAudio ? (
                <div className="loading-message">Loading campaign audio...</div>
            ) : campaignAudio.length === 0 ? (
                <div className="no-items-message">
                    No audio files found in this campaign.
                    <br />Upload some audio using the "Upload New" tab.
                </div>
            ) : (
                <div className="campaign-items-grid">
                    {campaignAudio.map((audio, index) => (
                        <div 
                            key={index} 
                            className={`campaign-library-item${varVal?.filename === audio.filename ? ' selected' : ''}`}
                            onClick={() => handleLibraryAudioSelect(audio)}
                        >
                            <div className="fl-column grow compact">
                                <div className="item-name">{convertUniqueFilenameToOriginal(audio.name)}</div>
                                <AudioPreview src={audio.url} index={index} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Show selected image details */}
            {varVal && currentTab === 'library' && (
                <div className="selected-item-info">
                    <div className="file-name">Selected: {convertUniqueFilenameToOriginal(varVal.originalFile)}</div>
                </div>
            )}
        </div>
    );

    return (
        <TabbedPanel
            tabs={tabData}
            switchTabCallback={handleTabSwitch}
            initialTab={0}
            tabWidth="100%"
            tabStyle={2}
        >
            <>
                {currentTab === 'upload' &&
                    <AudioUploaderV2
                        id={"temporary-audio-upload"}
                        currentFile={varVal?.srcUrl || ""}
                        currentFileName={varVal.originalFile || "Unknown"}
                        fileData={varVal}
                        fileSelectedCallback={
                            (data) => { 
                                console.log('TemporaryAudioUploadPopup fileSelectedCallback', data);
                                if (data.originalEvent !== 'removed') {
                                    const fileData = data.file;
                                    // Create a blob url for the uploaded file
                                    const blobUrl = URL.createObjectURL(fileData);
                                    varVal.srcUrl = blobUrl;
                                    varVal.newFile = fileData;
                                    varVal.originalFile = fileData.name;
                                    varVal.isDefault = false;
                                    varVal.filename = fileData.name;
                                    // That's all we need to do here as we are not going to actually upload this image now, we will handle it properly later
                                    // (this is only a temporary image upload and we are not saving our data yet)

                                    // Trigger both callbacks to update UI and right sidebar preview
                                    if (props.onAudioSelected) {
                                        props.onAudioSelected(varVal);
                                    }
                                    if (props.onAudioChanged) {
                                        props.onAudioChanged(varVal);
                                    }
                                }
                            }
                        }
                        showRemoveOption={false}
                        uploadImmediately={false}
                        showLabel={true}
                        label={props.controlLabel}
                        labelNote={props.labelNote}
                    />
                }
                {currentTab === 'library' && renderLibraryTab()}
            </>
        </TabbedPanel>
    );
}

export default AudioUploaderContent;